import React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaStore,
  FaLock,
  FaCheckCircle,
  FaArrowRight,
  FaBolt,
  FaTags,
  FaPercent,
  FaBoxes,
  FaImage,
  FaGlobe
} from 'react-icons/fa';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { CENTRO_COMERCIAL_MODULES } from './modulesRegistry';

const MODULE_ICONS = {
  'lista-expres': FaBolt,
  'flyers-material': FaImage,
  'gestion-listas': FaTags,
  promociones: FaPercent,
  combos: FaBoxes,
  'catalogo-online': FaGlobe
};

const ComingSoon = ({ moduleMeta }) => (
  <Card>
    <div className="text-center py-12 space-y-3">
      <FaLock className="mx-auto h-12 w-12 text-e0e3-muted" />
      <h2 className="text-xl font-semibold text-e0e3-foreground">
        {moduleMeta?.label || 'Módulo'}
      </h2>
      <p className="text-e0e3-muted max-w-md mx-auto">
        {moduleMeta?.description || 'Disponible en un sprint posterior.'}
      </p>
      <p className="text-sm text-e0e3-muted">Próximamente · sin implementación aún</p>
      <Link
        to="/centro-comercial"
        className="inline-block mt-4 text-e0e3-accent-soft hover:underline"
      >
        Volver al Centro Comercial
      </Link>
    </div>
  </Card>
);

/**
 * Hub Centro Comercial — capa UX sobre herramientas existentes.
 * No reemplaza motores de listas, promociones, combos ni Lista Exprés.
 */
const CentroComercialHub = () => {
  const navigate = useNavigate();
  const { modulo } = useParams();

  if (modulo) {
    const moduleMeta = CENTRO_COMERCIAL_MODULES.find((m) => m.id === modulo);
    if (moduleMeta?.status === 'active' && moduleMeta.path) {
      return <Navigate to={moduleMeta.path} replace />;
    }
    if (!moduleMeta || moduleMeta.status === 'planned') {
      return (
        <div className="space-y-6" data-testid="centro-comercial-coming-soon">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-e0e3-foreground">Centro Comercial</h1>
            <Button
              color="secondary"
              icon={<FaArrowLeft />}
              onClick={() => navigate('/centro-comercial')}
            >
              Volver
            </Button>
          </div>
          <ComingSoon moduleMeta={moduleMeta || { label: modulo }} />
        </div>
      );
    }
  }

  return (
    <div className="space-y-8" data-testid="centro-comercial-hub">
      <section className="relative overflow-hidden rounded-2xl border border-e0e3-accent/35 bg-gradient-to-br from-e0e3-accent/15 via-e0e3-surface/70 to-e0e3-surface/40 p-6 sm:p-8 lg:p-10">
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-e0e3-accent/40 bg-e0e3-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-e0e3-accent-soft mb-4">
            <FaStore className="h-3.5 w-3.5" />
            Comercial
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-e0e3-foreground tracking-tight">
            Centro Comercial
          </h1>
          <p className="mt-3 text-base sm:text-lg text-e0e3-muted leading-relaxed max-w-2xl">
            Todo lo que necesitás para mostrar, promocionar y vender mejor.
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
        {CENTRO_COMERCIAL_MODULES.map((mod) => {
          const active = mod.status === 'active';
          const Icon = MODULE_ICONS[mod.id] || FaStore;
          const content = (
            <Card
              className={`h-full transition border min-h-[11rem] ${
                active
                  ? 'border-e0e3-accent/45 hover:border-e0e3-accent hover:bg-white/[0.03] cursor-pointer'
                  : 'border-white/10 opacity-85'
              } ${mod.featured ? 'md:col-span-1 ring-1 ring-e0e3-accent/20' : ''}`}
            >
              <div className="flex flex-col h-full gap-4 p-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`shrink-0 rounded-xl p-3 ${
                        active ? 'bg-e0e3-accent/20 text-e0e3-accent-soft' : 'bg-white/5 text-e0e3-muted'
                      }`}
                    >
                      <Icon className="h-6 w-6" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-xl font-semibold text-e0e3-foreground">{mod.label}</h2>
                      <p className="text-sm sm:text-base text-e0e3-muted mt-1.5 leading-snug">
                        {mod.description}
                      </p>
                    </div>
                  </div>
                  {active ? (
                    <span className="inline-flex items-center gap-1 shrink-0 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-200">
                      <FaCheckCircle className="h-3 w-3" />
                      Disponible
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 shrink-0 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-e0e3-muted">
                      <FaLock className="h-3 w-3" />
                      Próximamente
                    </span>
                  )}
                </div>
                {active ? (
                  <div className="mt-auto flex items-center gap-2 text-sm font-medium text-e0e3-accent-soft">
                    Abrir
                    <FaArrowRight className="h-3.5 w-3.5" />
                  </div>
                ) : (
                  <p className="mt-auto text-xs text-e0e3-muted">Aún no disponible</p>
                )}
              </div>
            </Card>
          );

          return (
            <Link
              key={mod.id}
              to={mod.path}
              className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-e0e3-accent rounded-xl"
              data-testid={`centro-comercial-card-${mod.id}`}
              aria-label={`${mod.label}${active ? '' : ' (próximamente)'}`}
            >
              {content}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default CentroComercialHub;
